import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { noop } from 'lodash';
import Icon from 'react-fontawesome';
import styled, { css } from 'styled-components';

import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useIsMobile from 'hooks/useIsMobile';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

import { buildGridTemplateTracks } from './table/buildGridTemplateColumns';
import CircularProgress from './CircularProgress';

export enum DataTableTheme {
  DEFAULT = 'default',
  OPEN = 'open',
}
interface TableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
}

type DataTableProps<T> = TableProps<T> & {
  defaultSortBy?: SortingState;
  // Controlled sorting: pass both to drive sorting from the parent (e.g. server-side sort).
  // When omitted, the table sorts its own loaded rows client-side via internal state.
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  // Disable client-side sorting; the rows arrive pre-sorted from the server.
  manualSorting?: boolean;
  desktopOnlyColumns?: string[];
  columnVisibility?: Record<string, boolean>;
  hasMore?: boolean;
  loading?: boolean;
  drillable?: boolean;
  hasFooter?: boolean;
  theme?: DataTableTheme;
  maxHeight?: string;
  freezeFirstColumn?: boolean;
  setHoveredRowId?: (hoveredRowId: string) => void;
  onRowClick?: (row: Row<T>) => void;
  onBodyEndReached?: () => void;
  rowStyle?: (row: Row<T>) => React.CSSProperties | undefined;
  // Hierarchical rows: when provided, react-table treats returned children as nested sub-rows that
  // sort within their parent group and render in flattened expanded order.
  getSubRows?: (row: T) => T[] | undefined;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
};

// On small screens the frozen first column is capped at half the container so it can't crowd out
// the scrolling columns. Mirrors the old per-cell `max-width: 50cqw` rule at the track level.
export const buildMobileGridTemplateColumns = (tracks: string[], firstColumnSize: number) =>
  [`minmax(min(${firstColumnSize}px, 50cqw), 50cqw)`, ...tracks.slice(1)].join(' ');

export default function DataTable<T>(props: DataTableProps<T>) {
  const { customRef, isIntersecting } = useIntersectionObserver();
  const {
    defaultSortBy = [],
    sorting: controlledSorting,
    onSortingChange: controlledOnSortingChange,
    manualSorting = false,
    desktopOnlyColumns = [],
    columnVisibility = {},
    hasMore = false,
    loading,
    drillable = false,
    setHoveredRowId = noop,
    onRowClick,
    onBodyEndReached,
    rowStyle,
    hasFooter = true,
    theme = DataTableTheme.DEFAULT,
    maxHeight,
    freezeFirstColumn = false,
    getSubRows,
    expanded,
    onExpandedChange,
    ...reactTableProps
  } = props;

  const [internalSorting, setInternalSorting] = useState<SortingState>(defaultSortBy);
  const sorting = controlledSorting ?? internalSorting;
  const handleSortingChange = controlledOnSortingChange ?? setInternalSorting;

  const isMobile = useIsMobile();

  const table = useReactTable({
    ...reactTableProps,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
    onSortingChange: handleSortingChange,
    ...(getSubRows ? { getSubRows, getExpandedRowModel: getExpandedRowModel(), onExpandedChange } : {}),
    state: {
      columnVisibility: {
        ...desktopOnlyColumns.reduce((acc, key) => ({ ...acc, [key]: !isMobile }), {}),
        ...columnVisibility,
      },
      sorting,
      ...(expanded !== undefined ? { expanded } : {}),
    },
  });

  useEffect(() => {
    if (isIntersecting && hasMore && !loading) {
      onBodyEndReached?.();
    }
  }, [hasMore, isIntersecting, loading, onBodyEndReached]);

  const handleRowClick = (row: Row<T>) => {
    if (drillable && onRowClick) {
      onRowClick(row);
    }
  };

  const hasTableRows = !!table.getRowModel().rows.length;

  // Remember how tall the populated table was. A refetch that briefly empties the data (e.g. a
  // server-side sort) would otherwise collapse the table to a bare spinner, shrinking the page and
  // yanking the scroll position to the top. Holding the prior height as pad space keeps the card in
  // place until the new results arrive.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const lastHeightRef = useRef(0);
  useLayoutEffect(() => {
    if (hasTableRows && scrollContainerRef.current) {
      lastHeightRef.current = scrollContainerRef.current.offsetHeight;
    }
  });

  const visibleColumns = table.getVisibleLeafColumns();
  const tracks = buildGridTemplateTracks(visibleColumns);
  const mobileGridTemplateColumns =
    freezeFirstColumn && visibleColumns.length
      ? buildMobileGridTemplateColumns(tracks, visibleColumns[0].getSize())
      : undefined;

  return loading && !hasTableRows ? (
    <LoadingPlaceholder style={lastHeightRef.current ? { minHeight: lastHeightRef.current } : undefined}>
      <StyledCircularProgress />
    </LoadingPlaceholder>
  ) : (
    <ScrollContainer ref={scrollContainerRef} $theme={theme} $maxHeight={maxHeight}>
      <Table
        $theme={theme}
        $freezeFirstColumn={freezeFirstColumn}
        $gridTemplateColumns={tracks.join(' ')}
        $mobileGridTemplateColumns={mobileGridTemplateColumns}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={theme}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <ArrowUpDownIcon
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>

        <TableBody>
          {!hasTableRows && (
            <TableRow>
              <TableCell colSpan={table.getVisibleFlatColumns().length} style={{ justifyContent: 'center' }}>
                No rows found
              </TableCell>
            </TableRow>
          )}
          {hasTableRows &&
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                drillable={drillable}
                style={rowStyle?.(row)}
                onMouseEnter={() => setHoveredRowId(row.id)}
                onMouseLeave={() => setHoveredRowId('')}
                onClick={() => handleRowClick(row)}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} {...cell.column.columnDef.meta}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {/* Empty row used to trigger when the end is reached */}
          <HiddenTableRow ref={customRef}></HiddenTableRow>
          {hasTableRows && loading && (
            <TableRow>
              <TableCell colSpan={table.getVisibleFlatColumns().length}>
                <StyledCircularProgress />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {hasTableRows && hasFooter && (
          <TableFooter>
            {table.getFooterGroups().map((footerGroup) => (
              <TableHeaderFooterRow key={footerGroup.id} $theme={theme}>
                {footerGroup.headers.map((footer) => (
                  <TableFooterCell key={footer.id} {...footer.column.columnDef.meta}>
                    {flexRender(footer.column.columnDef.footer, footer.getContext())}
                  </TableFooterCell>
                ))}
              </TableHeaderFooterRow>
            ))}
          </TableFooter>
        )}
      </Table>
    </ScrollContainer>
  );
}

const StyledCircularProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.blue100};
  margin: auto;
`;

// Holds the populated table's height while a refetch empties the data, so the card keeps its size
// instead of collapsing and shifting the page scroll.
const LoadingPlaceholder = styled.div`
  display: flex;
  width: 100%;
`;

export const ScrollContainer = styled.div<{ $theme: DataTableTheme; $maxHeight?: string }>`
  overflow: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  container-type: inline-size;
  border-radius: 4px;
  max-height: ${({ $maxHeight }) => $maxHeight || '440px'};
  ${({ $theme }) =>
    $theme === DataTableTheme.DEFAULT &&
    css`
      border: 1px solid ${({ theme }) => theme.colors.black10};
    `}
`;

export const Table = styled.table<{
  $theme: DataTableTheme;
  $freezeFirstColumn?: boolean;
  $gridTemplateColumns: string;
  $mobileGridTemplateColumns?: string;
}>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  font-size: 12px;
  position: relative;

  /* Single full-width cells (empty state, loading row) span every track but stay pinned to the
     visible viewport width so their centered content lands in view rather than in the middle of a
     horizontally-overflowing grid. */
  td[colspan] {
    grid-column: 1 / -1;
    position: sticky;
    left: 0;
    width: 100cqw;
    justify-content: center;
  }

  /* Header and footer rows are real subgrid boxes, so their dividers paint at row level and span
     the full track width even when the columns overflow horizontally. */
  thead tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  }
  tfoot tr {
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }

  ${({ $freezeFirstColumn, $mobileGridTemplateColumns }) =>
    $freezeFirstColumn &&
    css`
      /* Freeze the first cell of each row during horizontal scroll. Solid white background so
         the translucent row tints (header black03, sub-row black05, hover primaryBlue05) don't
         let scrolled cells bleed through. The first cell anchors its sticky-left against its real
         row box (thead/tbody/tfoot are all subgrid boxes), so it travels the full scroll range. */
      tr > td:first-child {
        position: sticky;
        left: 0;
        background-color: ${({ theme }) => theme.colors.white};
      }

      thead tr > td:first-child {
        z-index: 3;
      }

      ${$mobileGridTemplateColumns &&
      css`
        @media (max-width: 800px) {
          grid-template-columns: ${$mobileGridTemplateColumns};
        }
      `}
    `}
`;

export const TableHeader = styled.thead`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  position: sticky;
  top: 0;
  z-index: 2;
`;

/* A real box (not display: contents) so it can paint the white base layer that translucent row
   tints (rowStyle sub-row shading, drillable hover) composite onto, spanning the full track
   width even when the columns overflow horizontally. */
export const TableBody = styled.tbody`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  background-color: ${({ theme }) => theme.colors.white};

  > * + * {
    border-top: 1px solid ${({ theme }) => theme.colors.black10};
  }
`;

export const TableRow = styled.tr<{ drillable?: boolean }>`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
  ${({ drillable = false, theme }) =>
    drillable &&
    `
  cursor: pointer;
  &:hover {
    background: ${theme.colors.blue5};
  }
    `}
`;

const HiddenTableRow = styled(TableRow)`
  border: none;
  height: 1px; /* Needs height to trigger the intersection */
`;

export const TableHeaderFooterRow = styled(TableRow)<{ $theme: DataTableTheme }>`
  ${({ $theme }) =>
    $theme === DataTableTheme.DEFAULT &&
    css`
      background-color: ${({ theme }) => theme.colors.white};
      background-image: linear-gradient(${({ theme }) => theme.colors.black3}, ${({ theme }) => theme.colors.black3});
    `}
`;

export const TableFooter = styled.tfoot`
  display: contents;
`;

export const TableSortIcon = styled(Icon)`
  padding-left: 8px;
  padding-top: 1px;
`;

export const TableCell = styled.td<{ bold?: boolean; padding?: string }>`
  align-items: center;
  display: flex;
  min-width: 0;
  overflow-wrap: anywhere;
  padding: ${({ padding }) => (padding ? padding : '16px 24px')};
  text-overflow: ellipsis;
  white-space: initial;
`;

export const TableFooterCell = styled(TableCell)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black100};
`;

export const TableHeaderCell = styled(TableCell)<{ isSorted: boolean; isSortable: boolean }>`
  align-items: normal;
  color: ${(props) => (props.isSorted ? props.theme.colors.black100 : props.theme.colors.black50)};
  font-weight: 500;
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'default')};
  background-color: ${({ theme }) => theme.colors.white};
  background-image: linear-gradient(${({ theme }) => theme.colors.black3}, ${({ theme }) => theme.colors.black3});
`;
